import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  RefreshCw, MessageSquare, Mail, CheckCircle, Plus, Clock, Truck, AlertTriangle,
  RotateCcw, Search, Package,
} from 'lucide-react';
import { montarTexto as montarTextoPadrao } from '../components/atendimento/textReplacer';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API_URL}/api/transp-pendencias`;

// Pendência de Transportadora: substitui a planilha "Pendências Transportadoras_2026".
// O acionamento segue o da Disponível para Retirada: copia o contato e o texto, a
// pessoa manda pelo WhatsApp/e-mail dela e confirma aqui — o ELO só registra.
const TRANSPS = ['Total', 'J&T', 'ASAP/CB'];

const MOTIVOS_ENCERRAR = [
  'Entregue', 'Reenviado', 'Estornado', 'Ressarcido pela transportadora', 'Em devolução', 'Outro',
];

const ACOES_RAPIDAS = [
  'Acompanhar a entrega', 'Abri chamado com a transportadora', 'Cobrei a transportadora',
  'Cliente confirmou o endereço', 'Seguido com reenvio',
];

// O fim da mensagem ao cliente depende do canal (Adnéia, 05/11/2026), só nesta tela:
// - WhatsApp: sem o nome de quem está logado; ficam "Atenciosamente," e "Atendimento WeConnect";
// - E-mail: o e-mail já tem assinatura automática, então saem a cortesia ("Seguimos à
//   disposição."), o "Atenciosamente,", o nome e a equipe. Frase que pede resposta fica.
// Mexe no texto padrão ANTES de preencher, para o nome nunca entrar. O texto à
// transportadora (passo 3) não passa por aqui.
const RE_ATENCIOSAMENTE = /^\s*atenciosamente\s*[,!.]?\s*$/i;
const RE_ASSINATURA_LINHA = /^\s*(\[ASSINATURA\]|equipe de atendimento weconnect\.?|atendimento weconnect\.?)\s*$/i;
const RE_CORTESIA = /^\s*(seguimos|permanecemos|permaneço|ficamos|fico)\s.*disposi[cç][aã]o/i;

function fimDoCanal(texto, canal) {
  const linhas = String(texto || '').replace(/\r\n?/g, '\n').split('\n');
  const semVaziasNoFim = (l) => { while (l.length && !l[l.length - 1].trim()) l.pop(); return l; };
  if (canal === 'Email') {
    let corte = -1;
    linhas.forEach((l, i) => { if (RE_ATENCIOSAMENTE.test(l)) corte = i; });
    let saida = corte >= 0 ? linhas.slice(0, corte) : linhas.filter(l => !RE_ASSINATURA_LINHA.test(l));
    saida = semVaziasNoFim(saida.map(l => l.replace(/\[ASSINATURA\]/g, '')));
    if (saida.length && RE_CORTESIA.test(saida[saida.length - 1])) saida.pop();
    return semVaziasNoFim(saida).join('\n');
  }
  const saida = [];
  linhas.forEach((l) => {
    const linha = RE_ASSINATURA_LINHA.test(l) ? 'Atendimento WeConnect' : l.replace(/\[ASSINATURA\]/g, '');
    if (linha === 'Atendimento WeConnect' && saida[saida.length - 1] === 'Atendimento WeConnect') return;
    saida.push(linha);
  });
  return saida.join('\n');
}

const GRUPO_TEXTO = {
  'Ag. Acionar o Cliente': 'Impossibilidade de entrega',
  'Enviado': 'Em processo de entrega',
  'Em devolução': 'Em devolução',
  'Ag. Confirmação de Entrega': 'Confirmar entrega',
};

const semAcento = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();

const fmtDia = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return d && m ? `${d}/${m}` : String(iso);
};

// tabelão: 'DD/MM/AAAA HH:MM:SS' -> 'DD/MM HH:MM'
const fmtPonto = (s) => {
  if (!s) return '';
  const m = String(s).match(/^(\d{2})\/(\d{2})\/\d{4}(?:\s+(\d{2}:\d{2}))?/);
  if (m) return `${m[1]}/${m[2]}${m[3] ? ' ' + m[3] : ''}`;
  return fmtDia(s);
};

const acaoCurta = (a) => {
  if (!a) return '';
  const dia = a.data ? String(a.data).slice(0, 5) : '';
  return dia ? `${dia} - ${a.texto}` : a.texto;
};

function corAcao(a) {
  if (!a) return 'text-slate-500';
  if (a.tipo === 'acionamento' || a.tipo === 'resposta') return 'text-emerald-700 dark:text-emerald-400';
  if (a.tipo === 'transportadora') return 'text-sky-700 dark:text-sky-400';
  if (a.tipo === 'encerramento') return 'text-slate-500';
  if (/acionar a transportadora de novo/i.test(a.texto || '')) return 'text-violet-700 dark:text-violet-400 font-medium';
  if (/acionar o cliente|cobrar o cliente/i.test(a.texto || '')) return 'text-amber-700 dark:text-amber-400 font-medium';
  return 'text-slate-700 dark:text-slate-300';
}

// O roteiro do acionamento (Adnéia, 29/10/2026; processos\pendencia-transportadora.md): o 1º
// contato de cada família de status e o texto de cada resposta do cliente. Os títulos são os de
// Textos Atendimento. Carga descartada é avaria; cancelado pelo remetente vai como recusa.
const ROTEIRO = {
  endereco: {
    primeiro: 'End. Não Localizado',
    respostas: [{ se: 'Mandou o endereço', texto: 'Endereço confirmado', endereco: true }],
  },
  recusa: {
    primeiro: 'Carga Recusada',
    respostas: [
      { se: 'Desconhece a recusa', texto: 'Recusa - confirmar endereço' },
      { se: 'Mandou o endereço', texto: 'Endereço confirmado', endereco: true },
      { se: 'Confirmou que recusou ou cancelou', texto: 'Recusa - perguntar o motivo' },
    ],
  },
  extravio: {
    primeiro: 'Extravio',
    respostas: [
      { se: '1 - Sim, recebeu', texto: 'Agradecer confirmação' },
      { se: '2 - Não recebeu', texto: 'Extravio - endereço p/ reenvio' },
      { se: 'Mandou o endereço', texto: 'Extravio - novo envio', endereco: true },
    ],
  },
};

// Motivo de Textos Atendimento do "Seguir com a entrega" de cada transportadora, e o lembrete
// de como ela recebe o pedido (ticket na J&T e no WB da Total; portal na ASAP).
const TRANSP_TEXTO = {
  'J&T': { motivo: 'Ag. Transportadora - J&T', dica: 'Abra o ticket na J&T com os dados abaixo e escreva o número do ticket antes de marcar feito.' },
  'Total': { motivo: 'Ag. Transportadora - Total', dica: 'Abra (ou confira) o ticket no WB. Se o cliente mudou o CEP da rua, a Total não muda: siga com a devolução e um novo envio.' },
  'ASAP/CB': { motivo: 'Ag. Transportadora - Asap', dica: 'A ASAP não gera ticket: a entrega anda no portal deles.' },
};
const MOTIVOS_DA_TRANSP = new Set(Object.values(TRANSP_TEXTO).map(t => t.motivo));

function familia(item) {
  const m = semAcento(`${item?.motivo_texto || ''} ${item?.ponto || ''}`);
  if (/EXTRAVI|AVARI|ROUB|DESCART/.test(m)) return 'extravio';
  if (/RECUS|CANCELADO PELO REMETENTE/.test(m)) return 'recusa';
  if (/AUSEN|ENDERECO|LOCALIZ|DESCONHEC|MUDOU|INCORRETO/.test(m)) return 'endereco';
  return null;
}

// Os filtros do dia (Adnéia, 29/10/2026): "Acionar transportadora" junta as que voltaram (6 dias
// úteis na 1ª vez, 2 nas cobranças, sem movimento) e as que o cliente já respondeu e ainda não
// foram à transportadora (no extravio o caminho é o novo envio); "Em acompanhamento" são as
// acionadas que a tela acompanha até o tabelão dar entregue.
const precisaTransportadora = (i) => i.situacao === 'aberto' && !i.acionar && (!!i.cobrar_transportadora
  || (!i.transportadora_acionada_em && familia(i) !== 'extravio' && !!(i.cliente_respondeu_em || i.endereco_confirmado)));
const emAcompanhamento = (i) => i.situacao === 'aberto' && !i.acionar && !!i.transportadora_acionada_em
  && !i.cobrar_transportadora;

// Texto sugerido para o 1º contato: o do roteiro da família; sem família, o "Enviado" da transportadora.
function tituloSugerido(item) {
  const f = familia(item);
  return f ? ROTEIRO[f].primeiro : item.transportadora_nome;
}

function copyToClipboard(text, mensagem = 'Copiado!') {
  const txt = String(text || '');
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(txt).then(() => toast.success(mensagem)).catch(() => toast.error('Erro ao copiar'));
    return;
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = txt;
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    if (ok) toast.success(mensagem); else toast.error('Erro ao copiar');
  } catch {
    toast.error('Erro ao copiar');
  }
}

export default function PendenciasTransportadora() {
  const { getAuthHeader, user } = useAuth();
  const [dados, setDados] = useState({ itens: [], resumo: {} });
  const [loading, setLoading] = useState(true);
  const [transp, setTransp] = useState('todas');
  const [filtro, setFiltro] = useState('acionar'); // acionar | acompanhamento | transportadora | aberto | encerrado
  const [busca, setBusca] = useState('');
  const [textos, setTextos] = useState([]);
  const [ticketEdit, setTicketEdit] = useState({});

  // modal Acionar
  const [modalAcionar, setModalAcionar] = useState(null);
  const [canal, setCanal] = useState('ZAP');
  const [tituloSel, setTituloSel] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [salvando, setSalvando] = useState(false);
  // roteiro: a resposta escolhida, o texto dela e o endereço que o cliente informou
  const [respostaSel, setRespostaSel] = useState(null);
  const [respostaMsg, setRespostaMsg] = useState('');
  const [endereco, setEndereco] = useState('');
  const [verTodos, setVerTodos] = useState(false);   // os textos do passo 1 ficam atrás de um botão
  const [ticketNovo, setTicketNovo] = useState('');

  // modal Histórico
  const [modalHist, setModalHist] = useState(null);
  const [novaAcao, setNovaAcao] = useState('');
  const [ciclos, setCiclos] = useState([]);

  // modal Encerrar
  const [modalEnc, setModalEnc] = useState(null);
  const [motivoEnc, setMotivoEnc] = useState('Entregue');
  const [obsEnc, setObsEnc] = useState('');

  // modal Inserir
  const [modalIns, setModalIns] = useState(false);
  const [insEntrega, setInsEntrega] = useState('');
  const [insPonto, setInsPonto] = useState('');
  const [insObs, setInsObs] = useState('');
  const [insAcionar, setInsAcionar] = useState(false);

  const carregar = useCallback(async ({ forcar = false, silencioso = false } = {}) => {
    if (!silencioso) setLoading(true);
    try {
      const res = await axios.get(BASE, {
        headers: getAuthHeader(),
        params: forcar ? { forcar: 1 } : {},
      });
      setDados(res.data || { itens: [], resumo: {} });
    } catch (e) {
      if (!silencioso) toast.error('Erro ao carregar: ' + (e?.response?.data?.detail || e.message));
    } finally {
      if (!silencioso) setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    const t = setInterval(() => carregar({ silencioso: true }), 120000);
    return () => clearInterval(t);
  }, [carregar]);
  useEffect(() => {
    axios.get(`${BASE}/textos`, { headers: getAuthHeader() })
      .then(r => setTextos(r.data || []))
      .catch(() => setTextos([]));
  }, [getAuthHeader]);

  const itens = useMemo(() => dados.itens || [], [dados.itens]);
  const resumo = dados.resumo || {};

  const daTransp = useCallback(
    (i) => transp === 'todas' || i.transportadora === transp, [transp]);

  const contagem = useMemo(() => {
    const base = itens.filter(daTransp);
    return {
      aberto: base.filter(i => i.situacao === 'aberto').length,
      acionar: base.filter(i => i.situacao === 'aberto' && i.acionar).length,
      acompanhamento: base.filter(emAcompanhamento).length,
      transportadora: base.filter(precisaTransportadora).length,
      encerrado: base.filter(i => i.situacao === 'encerrado').length,
    };
  }, [itens, daTransp]);

  const visiveis = useMemo(() => {
    const q = semAcento(busca.trim());
    let lst = itens.filter(daTransp).filter(i => {
      if (filtro === 'encerrado') return i.situacao === 'encerrado';
      if (filtro === 'acionar') return i.situacao === 'aberto' && i.acionar;
      if (filtro === 'transportadora') return precisaTransportadora(i);
      if (filtro === 'acompanhamento') return emAcompanhamento(i);
      return i.situacao === 'aberto';
    });
    if (q) {
      lst = lst.filter(i => semAcento([
        i.entrega, i.nota, i.rastreio, i.ticket, i.canal, i.nome_cliente, i.ponto, i.status_atual,
      ].join(' ')).includes(q));
    }
    if (filtro === 'encerrado') {
      lst = [...lst].sort((a, b) => String(b.data_encerramento || '').localeCompare(String(a.data_encerramento || '')));
    }
    return lst;
  }, [itens, daTransp, filtro, busca]);

  // ── textos do acionamento ──
  const textosDoItem = useCallback((item) => {
    if (!item) return [];
    const canalN = semAcento(item.canal);
    return textos.filter(t => {
      if (MOTIVOS_DA_TRANSP.has(t.motivo)) return false;   // esses vão no passo 3
      if (t.parceiro && !canalN.includes(semAcento(t.parceiro))) return false;
      if (t.motivo === 'Enviado') {
        return semAcento(t.titulo) === semAcento(item.transportadora_nome);
      }
      return true;
    });
  }, [textos]);

  const montarTexto = useCallback((item, t, { paraTransportadora = false, end = '', canal: canalMsg = 'ZAP' } = {}) => {
    if (!item || !t) return '';
    // ao cliente, o fim muda com o canal (fimDoCanal); à transportadora, o texto vai como está
    const base = paraTransportadora ? t.texto : fimDoCanal(t.texto, canalMsg);
    // acionamento vai direto ao cliente (WhatsApp ou e-mail): cumprimento com o primeiro nome;
    // o rastreio vazio aqui é de propósito (etiqueta interna), então não fica marcado.
    // À transportadora vai o rastreio dela mesmo que seja a etiqueta (ela conhece), mais o
    // telefone do cliente e o endereço que ele confirmou (campos só desta tela, 29/10/2026).
    const texto = montarTextoPadrao(base, {
      user,
      formData: { parceiro: item.canal, numero_pedido: item.entrega },
      pedidoErp: {
        nome_cliente: item.nome_cliente,
        produto: item.produto,
        itens_entrega: item.itens_entrega,
        numero_pedido: item.entrega,
        nota_fiscal: item.nota,
        chave_nota: item.chave_nota,
        // a etiqueta é código interno da transportadora: não vai no texto ao cliente
        // (mesma regra da Retirada); ASAP rastreia pela nota
        codigo_rastreio: paraTransportadora ? item.rastreio
          : (item.transportadora === 'ASAP/CB' || item.rastreio_tipo === 'etiqueta' ? '' : item.rastreio),
        data_status: item.data_ultimo_ponto,
        transportadora: item.transportadora_nome,
        status_pedido: item.motivo_texto,
      },
    }, { para: 'cliente', marcarRastreioVazio: false }).texto;
    return texto
      .split('[TELEFONE_CLIENTE]').join(item.fone_cliente || '?')
      .split('[ENDERECO_ENTREGA]').join(end.trim() || '[ENDERECO_ENTREGA]');
  }, [user]);

  const abrirAcionar = (item) => {
    const lista = textosDoItem(item);
    const sug = tituloSugerido(item);
    const t = lista.find(x => x.titulo === sug) || lista[0];
    const c = item.fone_cliente ? 'ZAP' : 'Email';
    setModalAcionar(item);
    setCanal(c);
    setTituloSel(t ? t.titulo : '');
    setMensagem(t ? montarTexto(item, t, { canal: c }) : '');
    setRespostaSel(null);
    setRespostaMsg('');
    setEndereco(item.endereco_confirmado || '');
    setVerTodos(false);
    setTicketNovo('');
  };

  // ── roteiro: a resposta do cliente e a transportadora ──
  const acharTexto = (titulo) => textos.find(t => !MOTIVOS_DA_TRANSP.has(t.motivo) && t.titulo === titulo);
  const textoTransp = (item) => {
    const tt = item && TRANSP_TEXTO[item.transportadora];
    return tt ? textos.find(t => t.motivo === tt.motivo) : null;
  };

  const escolherResposta = (r) => {
    const t = acharTexto(r.texto);
    setRespostaSel(r);
    setRespostaMsg(t ? montarTexto(modalAcionar, t, { canal }) : '');
  };

  // Trocar WhatsApp ↔ e-mail refaz a mensagem com o fim do canal novo. Mensagem que a
  // pessoa já editou fica como está (não se perde o que ela escreveu).
  const textoDoTitulo = (titulo) =>
    textosDoItem(modalAcionar).find(x => x.titulo === titulo) || acharTexto(titulo);
  const trocarCanal = (novo) => {
    if (novo === canal) return;
    const t1 = tituloSel ? textoDoTitulo(tituloSel) : null;
    if (t1 && mensagem === montarTexto(modalAcionar, t1, { canal })) {
      setMensagem(montarTexto(modalAcionar, t1, { canal: novo }));
    }
    const t2 = respostaSel ? acharTexto(respostaSel.texto) : null;
    if (t2 && respostaMsg === montarTexto(modalAcionar, t2, { canal })) {
      setRespostaMsg(montarTexto(modalAcionar, t2, { canal: novo }));
    }
    setCanal(novo);
  };

  const postarRoteiro = async (rota, corpo, ok) => {
    if (!modalAcionar) return;
    setSalvando(true);
    try {
      await axios.post(`${BASE}/${modalAcionar.id}/${rota}`, corpo, { headers: getAuthHeader() });
      toast.success(ok);
      setModalAcionar(null);
      carregar({ silencioso: true });
    } catch (e) {
      toast.error('Erro: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSalvando(false);
    }
  };
  const registrarResposta = () => postarRoteiro('resposta',
    { canal, texto_padrao: respostaSel?.texto || '', endereco: respostaSel?.endereco ? endereco : '' },
    `Resposta via ${canal === 'ZAP' ? 'WhatsApp' : 'e-mail'} registrada`);
  const gravarEndereco = () => postarRoteiro('endereco', { endereco }, 'Endereço gravado na linha');
  const registrarTransportadora = () => postarRoteiro('acionar-transportadora',
    { texto_padrao: textoTransp(modalAcionar)?.titulo || '', endereco, ticket: ticketNovo },
    'Feito: acionamento da transportadora registrado');

  const escolherTexto = (t) => {
    setTituloSel(t.titulo);
    setMensagem(montarTexto(modalAcionar, t, { canal }));
  };

  const confirmarAcionar = async () => {
    if (!modalAcionar) return;
    setSalvando(true);
    try {
      await axios.post(`${BASE}/${modalAcionar.id}/acionar`, { canal, texto_padrao: tituloSel },
        { headers: getAuthHeader() });
      toast.success(`Acionamento via ${canal === 'ZAP' ? 'WhatsApp' : 'e-mail'} registrado`);
      setModalAcionar(null);
      carregar({ silencioso: true });
    } catch (e) {
      toast.error('Erro: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSalvando(false);
    }
  };

  // ── histórico / ações ──
  const abrirHist = async (item) => {
    setModalHist(item);
    setNovaAcao('');
    setCiclos([]);
    if ((item.ciclo || 1) > 1) {
      try {
        const r = await axios.get(`${BASE}/${item.id}/ciclos`, { headers: getAuthHeader() });
        setCiclos((r.data || []).filter(c => c.id !== item.id));
      } catch { /* sem histórico anterior */ }
    }
  };

  const registrarAcao = async (dispensa = false) => {
    if (!modalHist) return;
    const texto = novaAcao.trim();
    if (!texto && !dispensa) { toast.error('Escreva a ação'); return; }
    setSalvando(true);
    try {
      const r = await axios.post(`${BASE}/${modalHist.id}/acao`, { texto, dispensa },
        { headers: getAuthHeader() });
      const acao = r.data?.acao;
      setModalHist(m => (m ? { ...m, acoes: [...(m.acoes || []), acao], acionar: dispensa ? false : m.acionar } : m));
      setNovaAcao('');
      toast.success('Ação registrada');
      carregar({ silencioso: true });
    } catch (e) {
      toast.error('Erro: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSalvando(false);
    }
  };

  // ── ticket ──
  const salvarTicket = async (item) => {
    const v = ticketEdit[item.id];
    if (v === undefined || v === (item.ticket || '')) return;
    try {
      await axios.put(`${BASE}/${item.id}/ticket`, { ticket: v }, { headers: getAuthHeader() });
      setDados(d => ({ ...d, itens: d.itens.map(i => (i.id === item.id ? { ...i, ticket: v.trim() } : i)) }));
      setTicketEdit(t => { const n = { ...t }; delete n[item.id]; return n; });
      toast.success(`Ticket da ${item.entrega} salvo`);
    } catch (e) {
      toast.error('Erro ao salvar ticket: ' + (e?.response?.data?.detail || e.message));
    }
  };

  // ── encerrar / reabrir ──
  const confirmarEncerrar = async () => {
    if (!modalEnc) return;
    if (motivoEnc === 'Outro' && !obsEnc.trim()) { toast.error('Escreva o motivo'); return; }
    setSalvando(true);
    try {
      await axios.post(`${BASE}/${modalEnc.id}/encerrar`,
        { motivo: motivoEnc === 'Outro' ? obsEnc.trim() : motivoEnc, obs: motivoEnc === 'Outro' ? '' : obsEnc },
        { headers: getAuthHeader() });
      toast.success(`Entrega ${modalEnc.entrega} encerrada`);
      setModalEnc(null);
      carregar({ silencioso: true });
    } catch (e) {
      toast.error('Erro: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSalvando(false);
    }
  };

  const reabrir = async (item) => {
    if (!window.confirm(`Reabrir a pendência da entrega ${item.entrega}? Ela só sai de novo pelo botão Encerrar.`)) return;
    try {
      await axios.post(`${BASE}/${item.id}/reabrir`, { obs: '' }, { headers: getAuthHeader() });
      toast.success('Pendência reaberta');
      carregar({ silencioso: true });
    } catch (e) {
      toast.error('Erro: ' + (e?.response?.data?.detail || e.message));
    }
  };

  // ── inserir manual ──
  const confirmarInserir = async () => {
    const ent = insEntrega.trim();
    if (!ent) { toast.error('Informe a entrega'); return; }
    setSalvando(true);
    try {
      const r = await axios.post(`${BASE}/inserir`,
        { entrega: ent, ponto: insPonto, obs: insObs, acionar: insAcionar },
        { headers: getAuthHeader() });
      if (r.data?.ja_existia) toast.info(`A entrega ${ent} já está em aberto`);
      else toast.success(`Entrega ${ent} inserida`);
      setModalIns(false);
      setInsEntrega(''); setInsPonto(''); setInsObs(''); setInsAcionar(false);
      carregar({ silencioso: true });
    } catch (e) {
      toast.error('Erro: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSalvando(false);
    }
  };

  const tjt = dados.tracking_jt || {};
  const textosModal = textosDoItem(modalAcionar);
  const gruposModal = textosModal.reduce((acc, t) => {
    const g = GRUPO_TEXTO[t.motivo] || t.motivo;
    (acc[g] = acc[g] || []).push(t);
    return acc;
  }, {});

  return (
    <div className="p-6 space-y-5">
      {/* Cabeçalho */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Truck className="h-6 w-6" /> Pendência de Transportadora
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Total, J&T e ASAP/CB. Entra sozinho o que pede acionar o cliente (pelo tabelão e pelo Tracking da J&T,
            que sobe todo dia na Base ELO, na atividade Importar dados); sai sozinho quando entrega, devolve ou cancela.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setModalIns(true)}>
              <Plus className="h-4 w-4 mr-2" /> Inserir manual
            </Button>
            <Button size="sm" onClick={() => carregar({ forcar: true })} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Atualizar
            </Button>
          </div>
          <div className="text-[11px] text-muted-foreground text-right">
            {dados.sincronizado_em && (
              <span>Conferido com o tabelão: {new Date(dados.sincronizado_em).toLocaleString('pt-BR')}</span>
            )}
            {tjt.arquivo && (
              <span className="ml-2">· Tracking J&T: {tjt.arquivo}
                {tjt.importado_em ? ` (${new Date(tjt.importado_em).toLocaleString('pt-BR')}${tjt.por ? ', ' + tjt.por : ''})` : ''}
              </span>
            )}
          </div>
        </div>
      </div>

      {dados.aviso && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" /> {dados.aviso}
        </div>
      )}

      {/* Cards por transportadora (clique filtra) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {TRANSPS.map(t => {
          const r = resumo[t] || {};
          const ativo = transp === t;
          return (
            <button key={t} type="button" onClick={() => setTransp(ativo ? 'todas' : t)}
              className={`text-left bg-white dark:bg-slate-800 rounded-lg border p-3 shadow-sm transition-colors ${
                ativo ? 'border-2 border-orange-500' : 'hover:border-slate-400'}`}>
              <div className="text-xs text-muted-foreground">{t}</div>
              <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                {r.abertas ?? 0} <span className="text-xs font-normal text-muted-foreground">em aberto</span>
              </div>
              <div className="text-xs mt-0.5">
                <span className="text-amber-700 dark:text-amber-400 font-medium">{r.acionar ?? 0} acionar o cliente</span>
                {(() => {
                  const n = itens.filter(i => i.transportadora === t && precisaTransportadora(i)).length;
                  return n > 0 && <span className="text-violet-700 dark:text-violet-400 font-medium"> · {n} acionar transportadora</span>;
                })()}
                <span className="text-muted-foreground"> · hoje +{r.entraram_hoje ?? 0} / −{r.sairam_hoje ?? 0}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Filtros */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { key: 'acionar', label: `Acionar o cliente (${contagem.acionar})` },
          { key: 'acompanhamento', label: `Em acompanhamento (${contagem.acompanhamento})` },
          { key: 'transportadora', label: `Acionar transportadora (${contagem.transportadora})` },
          { key: 'aberto', label: `Em aberto (${contagem.aberto})` },
          { key: 'encerrado', label: `Encerrados — ${dados.encerrados_dias || 15} dias (${contagem.encerrado})` },
        ].map(f => (
          <button key={f.key} onClick={() => setFiltro(f.key)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              filtro === f.key
                ? 'bg-slate-800 text-white border-slate-800 dark:bg-slate-100 dark:text-slate-900'
                : 'border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300'
            }`}>
            {f.label}
          </button>
        ))}
        {transp !== 'todas' && (
          <button onClick={() => setTransp('todas')} className="text-xs text-blue-600 hover:underline">
            mostrar todas as transportadoras
          </button>
        )}
        <div className="ml-auto relative">
          <Search className="h-4 w-4 absolute left-2.5 top-2 text-slate-400" />
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Entrega, nota, rastreio, ticket..."
            className="pl-8 pr-3 py-1.5 text-sm border rounded-lg bg-background w-64" />
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border shadow-sm overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground">
            <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" /> Carregando...
          </div>
        ) : visiveis.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <Package className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p>Nenhuma pendência aqui</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900/40 border-b">
              <tr>
                {['Entrou', 'Entrega', 'Ponto', 'Rastreio', 'Nota', 'Canal', 'Status atual', 'Últ. ponto', 'Ticket',
                  filtro === 'encerrado' ? 'Encerramento' : 'Ações', ''].map((h, k) => (
                  <th key={k} className="text-left px-3 py-3 font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visiveis.map(i => {
                const ult = (i.acoes || [])[(i.acoes || []).length - 1];
                const aberto = i.situacao === 'aberto';
                const ausente = i.tentativas_ausente > 0 && /AUSEN/.test(semAcento(`${i.ponto} ${i.status_atual}`));
                return (
                  <tr key={i.id}
                    className={`border-b last:border-0 align-top ${
                      aberto && i.acionar ? 'bg-amber-50 dark:bg-amber-950/20'
                        : precisaTransportadora(i) ? 'bg-violet-50 dark:bg-violet-950/20'
                          : 'hover:bg-slate-50/50 dark:hover:bg-slate-700/30'}`}>
                    <td className="px-3 py-2.5 text-xs whitespace-nowrap">{fmtDia(i.data_entrada)}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <div className="font-mono text-xs text-slate-700 dark:text-slate-300">{i.entrega}</div>
                      <div className="flex gap-1 mt-0.5 flex-wrap">
                        {transp === 'todas' && (
                          <span className="text-[10px] px-1.5 rounded bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">{i.transportadora}</span>
                        )}
                        {i.atendimento && (
                          <span className="text-[10px] px-1.5 rounded bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300"
                            title={`Tem atendimento no ELO${i.atendimento.motivo ? ' — ' + i.atendimento.motivo : ''}`}>
                            🎫 {i.atendimento.solicitacao || 'Atendimento'}
                          </span>
                        )}
                        {precisaTransportadora(i) && (
                          <span className="text-[10px] px-1.5 rounded bg-violet-100 text-violet-800"
                            title={i.cobrar_transportadora
                              ? 'A transportadora foi acionada e a entrega não movimentou no prazo'
                              : 'O cliente respondeu: falta acionar a transportadora'}>
                            🚚 {i.cobrar_transportadora ? 'acionar de novo' : 'acionar'}
                          </span>
                        )}
                        {emAcompanhamento(i) && (
                          <span className="text-[10px] px-1.5 rounded bg-sky-50 text-sky-700"
                            title={`Transportadora acionada em ${fmtDia(i.transportadora_acionada_em)}${i.transportadora_acionada_por ? ' por ' + i.transportadora_acionada_por : ''}: a tela acompanha até entregar`}>
                            👁 acompanhando
                          </span>
                        )}
                        {i.endereco_confirmado && (
                          <span className="text-[10px] px-1.5 rounded bg-emerald-50 text-emerald-700"
                            title={`Endereço confirmado pelo cliente: ${i.endereco_confirmado}`}>📍 endereço</span>
                        )}
                        {i.inserido_manual && (
                          <span className="text-[10px] px-1.5 rounded bg-violet-50 text-violet-700" title="Inserida à mão: só sai pelo botão Encerrar">manual</span>
                        )}
                        {i.reaberto_manual && (
                          <span className="text-[10px] px-1.5 rounded bg-violet-50 text-violet-700" title="Reaberta à mão: só sai pelo botão Encerrar">reaberta</span>
                        )}
                        {(i.ciclo || 1) > 1 && (
                          <span className="text-[10px] px-1.5 rounded bg-slate-100 text-slate-500" title="Essa entrega já passou pela pendência antes">{i.ciclo}ª vez</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-xs max-w-[190px]">
                      {i.ponto}
                      {ausente && <span className="text-muted-foreground"> ({i.tentativas_ausente}ª tentativa)</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      {i.rastreio ? (
                        <button className="font-mono text-xs text-slate-700 dark:text-slate-300 hover:text-blue-600 whitespace-nowrap"
                          title={i.rastreio_tipo === 'etiqueta'
                            ? 'Etiqueta da transportadora (o JMS/AWB ainda não chegou pela base). Clique para copiar'
                            : `Copiar ${i.rastreio_tipo || 'rastreio'}`}
                          onClick={() => copyToClipboard(i.rastreio, 'Rastreio copiado!')}>
                          {i.rastreio_tipo === 'etiqueta' && (
                            <span className="text-[10px] mr-1 px-1 rounded bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300">etiq</span>
                          )}
                          {i.rastreio}
                        </button>
                      ) : <span className="text-xs text-slate-400 italic">—</span>}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs whitespace-nowrap">{i.nota}</td>
                    <td className="px-3 py-2.5 text-xs whitespace-nowrap">{i.canal}</td>
                    <td className="px-3 py-2.5 text-xs max-w-[170px]">
                      <div>{i.status_atual || '—'}</div>
                      {i.jt && (i.jt.status || i.jt.anomalia) && (
                        <div className="text-[10px] text-muted-foreground"
                          title={[i.jt.anomalia, i.jt.previsao ? 'Previsão ' + i.jt.previsao : '', i.jt.sla].filter(Boolean).join(' · ')}>
                          J&T: {i.jt.status}{i.jt.data_operacao ? ' · ' + fmtDia(i.jt.data_operacao) : ''}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-xs whitespace-nowrap">{fmtPonto(i.data_ultimo_ponto)}</td>
                    <td className="px-3 py-2.5">
                      <input
                        value={ticketEdit[i.id] ?? (i.ticket || '')}
                        onChange={e => setTicketEdit(t => ({ ...t, [i.id]: e.target.value }))}
                        onBlur={() => salvarTicket(i)}
                        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                        placeholder="—"
                        className="w-24 px-1.5 py-1 text-xs font-mono border rounded bg-background"
                      />
                    </td>
                    <td className="px-3 py-2.5 text-xs max-w-[240px]">
                      {filtro === 'encerrado' ? (
                        <button onClick={() => abrirHist(i)} className="text-left">
                          <div className="text-slate-700 dark:text-slate-300">{fmtDia(i.data_encerramento)} · {i.encerramento_motivo}</div>
                          <div className="text-[10px] text-muted-foreground">{i.encerrado_por}</div>
                        </button>
                      ) : (
                        <button onClick={() => abrirHist(i)} className="text-left w-full" title="Ver histórico e registrar ação">
                          <div className={`line-clamp-2 ${corAcao(ult)}`}>{acaoCurta(ult) || '—'}</div>
                          <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" /> {(i.acoes || []).length} no histórico
                          </div>
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5 justify-center">
                        {aberto ? (
                          <>
                            <button onClick={() => abrirAcionar(i)} title="Acionar cliente"
                              className={`p-1.5 rounded-md border transition-colors ${
                                i.acionar ? 'border-amber-400 bg-amber-100 text-amber-800 hover:bg-amber-200'
                                  : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'}`}>
                              <MessageSquare className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => { setModalEnc(i); setMotivoEnc('Entregue'); setObsEnc(''); }} title="Encerrar"
                              className="p-1.5 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100">
                              <CheckCircle className="h-3.5 w-3.5" />
                            </button>
                          </>
                        ) : (
                          <button onClick={() => reabrir(i)} title="Reabrir"
                            className="p-1.5 rounded-md border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100">
                            <RotateCcw className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Modal: Acionar cliente ── */}
      <Dialog open={!!modalAcionar} onOpenChange={() => setModalAcionar(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Acionar cliente — {modalAcionar?.nome_cliente || modalAcionar?.entrega}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="grid grid-cols-2 gap-2 text-sm bg-slate-50 dark:bg-slate-900/40 rounded-lg p-3 border">
              <div><span className="text-muted-foreground">Entrega:</span> <span className="font-mono font-medium">{modalAcionar?.entrega}</span></div>
              <div><span className="text-muted-foreground">Nota:</span> <span className="font-mono font-medium">{modalAcionar?.nota}</span></div>
              <div><span className="text-muted-foreground">Canal:</span> {modalAcionar?.canal}</div>
              <div><span className="text-muted-foreground">Transportadora:</span> {modalAcionar?.transportadora_nome}</div>
              <div className="col-span-2"><span className="text-muted-foreground">Motivo:</span> {modalAcionar?.motivo_texto}</div>
            </div>

            {modalAcionar?.atendimento && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-800 text-sm">
                🎫 Essa entrega tem atendimento no ELO ({modalAcionar.atendimento.solicitacao || 'sem nº'}). Confira antes de acionar.
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">Enviar via</label>
              <div className="flex gap-2">
                {['ZAP', 'Email'].map(t => (
                  <button key={t} onClick={() => trocarCanal(t)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      canal === t
                        ? t === 'ZAP' ? 'bg-green-600 text-white border-green-600' : 'bg-blue-600 text-white border-blue-600'
                        : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                    {t === 'ZAP' ? <MessageSquare className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
                    {t === 'ZAP' ? 'WhatsApp' : 'E-mail'}
                  </button>
                ))}
              </div>
            </div>

            {(() => {
              const zap = canal === 'ZAP';
              const contato = zap ? modalAcionar?.fone_cliente : modalAcionar?.email_cliente;
              return (
                <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${
                  zap ? 'bg-green-50 border-green-200 dark:bg-green-950/20' : 'bg-blue-50 border-blue-200 dark:bg-blue-950/20'}`}>
                  {zap ? <MessageSquare className="h-4 w-4 text-green-600" /> : <Mail className="h-4 w-4 text-blue-600" />}
                  <div className="flex-1">
                    <div className={`text-[11px] font-medium uppercase tracking-wide ${zap ? 'text-green-600' : 'text-blue-600'}`}>
                      {zap ? 'WhatsApp' : 'E-mail'}
                    </div>
                    <div className="text-base font-semibold">
                      {contato || <span className="text-sm font-normal italic opacity-70">{zap ? 'Telefone' : 'E-mail'} não informado</span>}
                    </div>
                  </div>
                  {contato && (
                    <button onClick={() => copyToClipboard(contato, zap ? 'Telefone copiado!' : 'E-mail copiado!')}
                      className="text-xs font-medium px-2 py-1 rounded border bg-white/60 hover:bg-white">📋 Copiar</button>
                  )}
                </div>
              );
            })()}

            {modalAcionar?.endereco_confirmado && (
              <div className="px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm">
                📍 Endereço confirmado pelo cliente: <strong>{modalAcionar.endereco_confirmado}</strong>
              </div>
            )}

            {/* Passo 1: o primeiro contato (ou a cobrança, se o cliente não respondeu em 2 dias úteis) */}
            <div className="border-t pt-3">
              <div className="text-sm font-semibold mb-1">1 · Contato com o cliente</div>
              <label className="block text-sm font-medium mb-1">Texto padrão <span className="text-xs font-normal text-muted-foreground">(vem de Textos Atendimento)</span></label>
              {textosModal.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">Nenhum texto cadastrado para esse caso.</p>
              ) : !verTodos ? (
                // só o texto do roteiro; os outros ficam atrás do botão (Adnéia, 29/10/2026)
                <div className="flex items-center gap-2 flex-wrap">
                  {tituloSel && (
                    <span className="px-2.5 py-1 rounded-md border text-xs border-orange-500 bg-orange-50 text-orange-800 dark:bg-orange-950/30 dark:text-orange-300">
                      {tituloSel}
                    </span>
                  )}
                  <button onClick={() => setVerTodos(true)} className="text-xs text-blue-600 hover:underline font-medium">
                    Ver todos os textos ({textosModal.length})
                  </button>
                </div>
              ) : Object.entries(gruposModal).map(([g, lista]) => (
                <div key={g} className="mb-1.5">
                  <div className="text-[11px] text-muted-foreground mb-1">{g}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {lista.map(t => (
                      <button key={`${t.motivo}|${t.causa}|${t.titulo}|${t.parceiro}`} onClick={() => escolherTexto(t)}
                        className={`px-2.5 py-1 rounded-md border text-xs transition-colors ${
                          tituloSel === t.titulo
                            ? 'border-orange-500 bg-orange-50 text-orange-800 dark:bg-orange-950/30 dark:text-orange-300'
                            : 'border-slate-300 text-slate-600 hover:bg-slate-50 dark:text-slate-300'}`}>
                        {t.titulo}{t.parceiro ? ` (${t.parceiro})` : ''}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {verTodos && (
                <button onClick={() => setVerTodos(false)} className="text-xs text-blue-600 hover:underline font-medium">
                  Esconder os textos
                </button>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium">Mensagem</label>
                <button onClick={() => copyToClipboard(mensagem, 'Texto copiado!')}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium">📋 Copiar texto</button>
              </div>
              <textarea rows={8} value={mensagem} onChange={e => setMensagem(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm font-mono dark:bg-slate-800 dark:border-slate-600" />
              <div className="flex justify-end mt-1.5">
                <Button size="sm" onClick={confirmarAcionar} disabled={salvando}
                  className={canal === 'ZAP' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}>
                  {salvando ? 'Registrando...' : `Registrar o contato via ${canal === 'ZAP' ? 'WhatsApp' : 'E-mail'}`}
                </Button>
              </div>
            </div>

            {/* Passo 2: o cliente respondeu (para a cobrança dos 2 dias úteis) */}
            {(() => {
              const f = familia(modalAcionar);
              const respostas = f ? ROTEIRO[f].respostas : [{ se: 'Mandou o endereço', texto: 'Endereço confirmado', endereco: true }];
              return (
                <div className="border-t pt-3 space-y-2">
                  <div className="text-sm font-semibold">2 · O cliente respondeu</div>
                  <div className="flex flex-wrap gap-1.5">
                    {respostas.map(r => (
                      <button key={r.se} onClick={() => escolherResposta(r)}
                        className={`px-2.5 py-1 rounded-md border text-xs transition-colors ${
                          respostaSel?.se === r.se
                            ? 'border-orange-500 bg-orange-50 text-orange-800 dark:bg-orange-950/30 dark:text-orange-300'
                            : 'border-slate-300 text-slate-600 hover:bg-slate-50 dark:text-slate-300'}`}>
                        {r.se} → {r.texto}
                      </button>
                    ))}
                  </div>
                  {respostaSel && (
                    <>
                      {acharTexto(respostaSel.texto) ? (
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="text-xs text-muted-foreground">Resposta ao cliente</label>
                            <button onClick={() => copyToClipboard(respostaMsg, 'Texto copiado!')}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium">📋 Copiar texto</button>
                          </div>
                          <textarea rows={6} value={respostaMsg} onChange={e => setRespostaMsg(e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 text-sm font-mono dark:bg-slate-800 dark:border-slate-600" />
                        </div>
                      ) : (
                        <p className="text-xs text-amber-700 italic">
                          Falta o texto "{respostaSel.texto}" em Textos Atendimento (Ag. Acionar o Cliente).
                        </p>
                      )}
                    </>
                  )}
                  <div>
                    <label className="text-xs text-muted-foreground">Endereço informado pelo cliente (fica gravado na linha)</label>
                    <textarea rows={2} value={endereco} onChange={e => setEndereco(e.target.value)}
                      placeholder="Rua, número, complemento, bairro, cidade, CEP da rua e ponto de referência"
                      className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-slate-800 dark:border-slate-600" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={gravarEndereco} disabled={salvando || !endereco.trim()}>
                      Gravar só o endereço
                    </Button>
                    <Button size="sm" onClick={registrarResposta} disabled={salvando || !respostaSel}
                      className={canal === 'ZAP' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}>
                      {`Registrar a resposta via ${canal === 'ZAP' ? 'WhatsApp' : 'E-mail'}`}
                    </Button>
                  </div>
                </div>
              );
            })()}

            {/* Passo 3: a transportadora segue com a entrega (no extravio, o caminho é o novo envio) */}
            {familia(modalAcionar) !== 'extravio' && (() => {
              const tt = TRANSP_TEXTO[modalAcionar?.transportadora];
              const t = textoTransp(modalAcionar);
              const msgT = t ? montarTexto(modalAcionar, t, { paraTransportadora: true, end: endereco }) : '';
              return (
                <div className="border-t pt-3 space-y-2">
                  <div className="text-sm font-semibold">3 · Acionar a transportadora ({modalAcionar?.transportadora_nome})</div>
                  {tt && <p className="text-xs text-muted-foreground">{tt.dica}</p>}
                  {/* Os dados de abertura do ticket, cada um com o seu Copiar (Adnéia, 29/10/2026) */}
                  <div className="grid grid-cols-1 gap-1 text-sm bg-slate-50 dark:bg-slate-900/40 rounded-lg p-2 border">
                    {[
                      [`Rastreio${modalAcionar?.rastreio_tipo ? ` (${modalAcionar.rastreio_tipo})` : ''}`, modalAcionar?.rastreio],
                      ['Entrega', modalAcionar?.entrega],
                      ['Telefone do cliente', modalAcionar?.fone_cliente],
                      ['Endereço', endereco.trim()],
                    ].map(([rot, val]) => (
                      <div key={rot} className="flex items-center justify-between gap-2">
                        <span><span className="text-muted-foreground">{rot}:</span>{' '}
                          <span className="font-mono">{val || '?'}</span></span>
                        {val && (
                          <button onClick={() => copyToClipboard(val, `${rot} copiado!`)}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap">📋 Copiar</button>
                        )}
                      </div>
                    ))}
                  </div>
                  {t ? (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs text-muted-foreground">{t.titulo}</label>
                        <button onClick={() => copyToClipboard(msgT, 'Texto copiado!')}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium">📋 Copiar texto</button>
                      </div>
                      <textarea rows={7} readOnly value={msgT}
                        className="w-full border rounded-lg px-3 py-2 text-sm font-mono bg-slate-50 dark:bg-slate-800 dark:border-slate-600" />
                    </div>
                  ) : (
                    <p className="text-xs text-amber-700 italic">
                      Falta o texto "Seguir com a entrega" em Textos Atendimento ({tt?.motivo || 'motivo da transportadora'}).
                    </p>
                  )}
                  {(() => {
                    // a J&T devolve um ticket, e sem ele não se registra; na Total é opcional; a ASAP não tem
                    const jt = modalAcionar?.transportadora === 'J&T';
                    const temTicket = !!(ticketNovo.trim() || modalAcionar?.ticket);
                    const falta = !endereco.trim() ? 'Escreva o endereço que o cliente confirmou'
                      : (jt && !temTicket ? 'Escreva o número do ticket da J&T' : '');
                    return (
                      <div className="flex items-end justify-end gap-2 flex-wrap">
                        {modalAcionar?.transportadora !== 'ASAP/CB' && (
                          <div>
                            <label className="block text-xs text-muted-foreground">
                              Ticket {jt ? 'da J&T (obrigatório)' : 'da Total (se houver)'}
                            </label>
                            <input value={ticketNovo} onChange={e => setTicketNovo(e.target.value)}
                              placeholder={modalAcionar?.ticket || 'nº do ticket'}
                              className={`w-44 px-2 py-1.5 text-sm font-mono border rounded bg-background ${
                                jt && !temTicket ? 'border-amber-400' : ''}`} />
                          </div>
                        )}
                        <Button size="sm" onClick={registrarTransportadora} disabled={salvando || !!falta}
                          className="bg-sky-600 hover:bg-sky-700" title={falta}>
                          Feito: transportadora acionada
                        </Button>
                      </div>
                    );
                  })()}
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalAcionar(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Modal: Histórico de ações ── */}
      <Dialog open={!!modalHist} onOpenChange={() => setModalHist(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ações — entrega {modalHist?.entrega}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            {modalHist?.situacao === 'aberto' && (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  {ACOES_RAPIDAS.map(a => (
                    <button key={a} onClick={() => setNovaAcao(a)}
                      className="px-2 py-0.5 rounded border text-xs text-slate-600 hover:bg-slate-50 dark:text-slate-300">{a}</button>
                  ))}
                </div>
                <textarea rows={2} value={novaAcao} onChange={e => setNovaAcao(e.target.value)}
                  placeholder="Ex.: Prazo alterado para 09/10"
                  className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-slate-800 dark:border-slate-600" />
                <div className="flex gap-2 justify-end">
                  {modalHist?.acionar && (
                    <Button variant="outline" size="sm" disabled={salvando} onClick={() => registrarAcao(true)}
                      title="Tira o amarelo sem registrar acionamento">
                      Não precisa acionar
                    </Button>
                  )}
                  <Button size="sm" disabled={salvando || !novaAcao.trim()} onClick={() => registrarAcao(false)}>
                    Registrar ação
                  </Button>
                </div>
              </div>
            )}
            <div className="space-y-1.5 max-h-80 overflow-y-auto">
              {[...(modalHist?.acoes || [])].reverse().map((a, k) => (
                <div key={a.id || k} className="flex items-start gap-2 bg-slate-50 dark:bg-slate-900/40 rounded-lg px-3 py-2 border">
                  <div className="text-[11px] text-muted-foreground whitespace-nowrap pt-0.5 w-24">{a.data || '—'}</div>
                  <div className="flex-1">
                    <div className={`text-sm ${corAcao(a)}`}>
                      {a.canal === 'ZAP' && <MessageSquare className="inline h-3.5 w-3.5 mr-1 text-green-600" />}
                      {a.canal === 'Email' && <Mail className="inline h-3.5 w-3.5 mr-1 text-blue-600" />}
                      {a.texto}
                    </div>
                    {a.por && <div className="text-[10px] text-muted-foreground">{a.por}</div>}
                  </div>
                </div>
              ))}
            </div>
            {ciclos.length > 0 && (
              <div className="border-t pt-2">
                <div className="text-xs font-medium text-muted-foreground mb-1">Passagens anteriores pela pendência</div>
                {ciclos.map(c => (
                  <div key={c.id} className="text-xs text-slate-600 dark:text-slate-300">
                    {fmtDia(c.data_entrada)} → {fmtDia(c.data_encerramento) || 'aberta'} · {c.ponto}
                    {c.encerramento_motivo ? ` · ${c.encerramento_motivo}` : ''}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Modal: Encerrar ── */}
      <Dialog open={!!modalEnc} onOpenChange={() => setModalEnc(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Encerrar pendência — {modalEnc?.entrega}</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              {MOTIVOS_ENCERRAR.map(m => (
                <button key={m} onClick={() => setMotivoEnc(m)}
                  className={`py-2 px-2 rounded-lg border text-sm transition-colors ${
                    motivoEnc === m ? 'bg-emerald-600 text-white border-emerald-600' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                  {m}
                </button>
              ))}
            </div>
            <input value={obsEnc} onChange={e => setObsEnc(e.target.value)}
              placeholder={motivoEnc === 'Outro' ? 'Qual o motivo?' : 'Observação (opcional)'}
              className="w-full px-3 py-2 text-sm border rounded bg-background" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalEnc(null)}>Cancelar</Button>
            <Button onClick={confirmarEncerrar} disabled={salvando} className="bg-emerald-600 hover:bg-emerald-700">Encerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Modal: Inserir manual ── */}
      <Dialog open={modalIns} onOpenChange={(o) => { if (!o) setModalIns(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="h-5 w-5" /> Inserir pendência manualmente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-sm text-muted-foreground">
              Para a entrega que não entrou sozinha. Os dados vêm do tabelão. Inserida à mão, ela só sai pelo botão Encerrar.
            </p>
            <div>
              <label className="text-xs font-medium text-slate-600">Entrega *</label>
              <input autoFocus value={insEntrega} onChange={e => setInsEntrega(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') confirmarInserir(); }}
                placeholder="ex.: 127167528" className="mt-1 w-full px-3 py-2 text-sm border rounded bg-background font-mono" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Ponto (opcional — sem preencher, usa o status do tabelão)</label>
              <input value={insPonto} onChange={e => setInsPonto(e.target.value)} placeholder="ex.: Cliente diz que não recebeu"
                className="mt-1 w-full px-3 py-2 text-sm border rounded bg-background" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Observação (opcional)</label>
              <input value={insObs} onChange={e => setInsObs(e.target.value)}
                className="mt-1 w-full px-3 py-2 text-sm border rounded bg-background" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={insAcionar} onChange={e => setInsAcionar(e.target.checked)} />
              Já entra como "Acionar o cliente"
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setModalIns(false)}>Cancelar</Button>
            <Button size="sm" onClick={confirmarInserir} disabled={salvando || !insEntrega.trim()}>
              {salvando ? 'Inserindo...' : 'Inserir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
